import { useCallback, useMemo, useRef, useState } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';

interface UsePersistentSelectionOptions {
  dataAll: Array<{ id?: string; [key: string]: any }>;
  totalCount: number;
  /** Field to use as the unique ID for each row (default: 'id') */
  idField?: string;
}

interface UsePersistentSelectionResult {
  /** Controlled rowSelection state for TanStack Table */
  rowSelection: RowSelectionState;
  /** Callback for TanStack's onRowSelectionChange */
  onRowSelectionChange: (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => void;
  /** Number of selected items (accounts for selectAll + excludedIds) */
  selectedCount: number;
  /** Whether all rows on the current page are selected */
  allPageSelected: boolean;
  /** Whether selectAll mode is active */
  selectAllMode: boolean;
  /** IDs excluded in selectAll mode */
  excludedIds: Set<string>;
  /** Array of individually selected IDs (only in non-selectAll mode) */
  selectedRows: Array<string | undefined>;
  /** All individually selected IDs as Set */
  allSelectedIds: Set<string>;
  /** Activate selectAll mode */
  handleSelectAll: () => void;
  /** Clear all selections */
  handleClearSelection: () => void;
}

/**
 * Hook for persistent row selection across paginated tables.
 *
 * Manages selection state in the PARENT and computes TanStack's
 * rowSelection object. The table becomes "controlled" — no internal
 * rowSelection state, no sync/restore useEffects, no infinite loops.
 *
 * Supports two modes:
 * - Individual: user selects rows one by one, persisted across pages
 * - SelectAll: all rows selected by default, user unchecks to exclude
 */
export function usePersistentSelection({
  dataAll,
  totalCount,
  idField = 'id',
}: UsePersistentSelectionOptions): UsePersistentSelectionResult {
  const [allSelectedIds, setAllSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  // Current page row IDs
  const currentPageIds = useMemo(
    () => new Set(dataAll.map((r) => r[idField]).filter(Boolean) as string[]),
    [dataAll, idField]
  );

  // Compute TanStack rowSelection from persistent state + current page data
  const rowSelection = useMemo<RowSelectionState>(() => {
    const selection: RowSelectionState = {};
    for (const row of dataAll) {
      const rowId = row[idField];
      if (!rowId) continue;
      if (selectAllMode) {
        selection[rowId] = !excludedIds.has(rowId);
      } else {
        selection[rowId] = allSelectedIds.has(rowId);
      }
    }
    return selection;
  }, [dataAll, selectAllMode, excludedIds, allSelectedIds, idField]);

  // Keep a ref of rowSelection for resolving TanStack updater functions
  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;

  // Refs for stable callback
  const selectAllModeRef = useRef(selectAllMode);
  selectAllModeRef.current = selectAllMode;
  const currentPageIdsRef = useRef(currentPageIds);
  currentPageIdsRef.current = currentPageIds;

  // Handle TanStack's onRowSelectionChange — stable callback (no deps)
  const onRowSelectionChange = useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      // Resolve the updater to get the new selection state
      const newSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(rowSelectionRef.current)
        : updaterOrValue;

      if (selectAllModeRef.current) {
        // In selectAll mode: unchecked rows go to excludedIds
        setExcludedIds((prev) => {
          const next = new Set(prev);
          for (const id of currentPageIdsRef.current) {
            if (newSelection[id]) {
              next.delete(id); // checked = not excluded
            } else {
              next.add(id); // unchecked = excluded
            }
          }
          return next;
        });
      } else {
        // Individual mode: sync allSelectedIds with new selection
        setAllSelectedIds((prev) => {
          const next = new Set(prev);
          // Remove all current page IDs first
          for (const id of currentPageIdsRef.current) {
            next.delete(id);
          }
          // Add back the ones that are selected
          for (const [id, selected] of Object.entries(newSelection)) {
            if (selected && currentPageIdsRef.current.has(id)) {
              next.add(id);
            }
          }
          return next;
        });
      }
    },
    [] // Stable — reads from refs
  );

  const handleSelectAll = useCallback(() => {
    setSelectAllMode(true);
    setExcludedIds(new Set());
    setAllSelectedIds(new Set());
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectAllMode(false);
    setExcludedIds(new Set());
    setAllSelectedIds(new Set());
  }, []);

  const selectedCount = selectAllMode
    ? totalCount - excludedIds.size
    : allSelectedIds.size;

  const allPageSelected = currentPageIds.size > 0 && (
    selectAllMode
      ? [...currentPageIds].every((id) => !excludedIds.has(id))
      : [...currentPageIds].every((id) => allSelectedIds.has(id))
  );

  const selectedRows = useMemo(() => [...allSelectedIds], [allSelectedIds]);

  return {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    allPageSelected,
    selectAllMode,
    excludedIds,
    selectedRows,
    allSelectedIds,
    handleSelectAll,
    handleClearSelection,
  };
}
